import React, {useState} from 'react';
import {MainLayout} from "../../components/MainLayout";
import './AuditLogPage.scss';

const AuditLogDashboard = ({
                               auditLogs,
                               loading,
                               pagination,
                               filters,
                               actionTypes,
                               actions,
                               roles,
                               userTypes,
                               onPageChange,
                               onPageSizeChange,
                               onFilterChange,
                               onSortChange,
                               onExport,
                               onRefresh,
                               tab
                           }) => {
    const [showFilters, setShowFilters] = useState(false);
    const [localFilters, setLocalFilters] = useState(filters);

    // Handle local filter changes
    const handleLocalFilterChange = (field, value) => {
        setLocalFilters({...localFilters, [field]: value});
    };

    // Apply filters
    const applyFilters = () => {
        onFilterChange(localFilters);
        setShowFilters(false); // Optionally hide filters after applying
    };

    // Reset filters
    const resetFilters = () => {
        const defaultFilters = {
            performedBy: '',
            performedUsername: '',
            performedByRole: '',
            performedByUserType: '',
            action: '',
            taxNumber: '',
            activityNumber: '',
            note: '',
            affectedUserId: '',
            affectedUser: '',
            affectedDeviceId: '',
            affectedDeviceName: '',
            startDate: '',
            endDate: '',
            sortField: 'timestamp',
            sortDirection: 'desc'
        };
        setLocalFilters(defaultFilters);
        onFilterChange(defaultFilters);
    };

    // Handle sort change
    const handleSort = (field) => {
        const direction = filters.sortField === field && filters.sortDirection === 'asc' ? 'desc' : 'asc';
        onSortChange(field, direction);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    // Format enum display
    const formatEnum = (enumValue) => {
        if (!enumValue) return '';

        // Handle the case where enumValue is already a string (from API)
        const stringValue = String(enumValue);

        // Replace underscores with spaces and capitalize each word
        return stringValue.replace(/_/g, ' ').replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    };

    // Get sort indicator
    const getSortIndicator = (field) => {
        if (filters.sortField !== field) return null;
        return filters.sortDirection === 'asc' ? '↑' : '↓';
    };

    // Determine the class for action type chips
    const getActionTypeClass = (action) => {
        if (!action) return 'chip-default';

        const actionStr = String(action);
        if (actionStr.includes('ADD') || actionStr.includes('CREATE')) return 'chip-success';
        if (actionStr.includes('UPDATE')) return 'chip-primary';
        if (actionStr.includes('DELETE')) return 'chip-error';
        if (actionStr.includes('ENABLE') || actionStr.includes('DISABLE')) return 'chip-warning';
        return 'chip-default';
    };

    // Get role type class
    const getRoleTypeClass = (role, userType) => {
        if (userType === 'ADMINISTRATION') return 'role-chip-admin';
        if (role === 'SUPER_ADMIN') return 'role-chip-super-admin';
        return 'role-chip-user';
    };

    // Render active filter badges
    const renderFilterBadges = () => {
        const activeBadges = [];

        Object.entries(filters).forEach(([key, value]) => {
            if (value && !['sortField', 'sortDirection'].includes(key)) {
                let displayName = key.charAt(0).toUpperCase() + key.slice(1);
                let displayValue = value;

                // Format special cases
                if (key === 'performedByRole' && roles.length) {
                    const role = roles.find(r => r.value === value);
                    if (role) displayValue = role.label;
                } else if (key === 'performedByUserType' && userTypes.length) {
                    const userType = userTypes.find(t => t.value === value);
                    if (userType) displayValue = userType.label;
                } else if (key === 'action' && actionTypes.length) {
                    const action = actions.find(a => a.value === value);
                    if (action) displayValue = action.label;
                }

                activeBadges.push(<span key={key} className="filter-badge">
            {`${displayName}: ${displayValue}`}
                    <button
                        onClick={() => {
                            const newFilters = {...filters, [key]: ''};
                            setLocalFilters(newFilters);
                            onFilterChange(newFilters);
                        }}
                    >
              ×
            </button>
          </span>);
            }
        });

        return activeBadges.length ? (<div className="filter-badges-container">
                <span className="filter-badges-label">Active filters:</span>
                {activeBadges}
                {activeBadges.length > 1 && (<button
                        className="btn btn-link"
                        onClick={resetFilters}
                    >
                        Clear all
                    </button>)}
            </div>) : null;
    };

    function getBrowserName(userAgent) {
        // Common browsers to detect
        const browsers = {
            'Chrome': /Chrome\/([0-9.]+)/,
            'Firefox': /Firefox\/([0-9.]+)/,
            'Safari': /Safari\/([0-9.]+)(?!.*Chrome)/,
            'Edge': /Edg(e)?\/([0-9.]+)/,
            'Opera': /OPR\/([0-9.]+)/,
            'IE': /MSIE|Trident/
        };

        // Check for each browser in the user agent string
        for (const [browser, regex] of Object.entries(browsers)) {
            if (regex.test(userAgent)) {
                return browser;
            }
        }

        // If no match is found, return the first part as a fallback
        return userAgent.split(' ')[0];
    }

    // Generate pagination buttons
    const renderPaginationButtons = () => {
        const buttons = [];
        const currentPage = pagination.page + 1;
        const totalPages = pagination.totalPages;

        // Always show first page
        buttons.push(<button
            key="first"
            className={`pagination-button ${currentPage === 1 ? 'active' : ''}`}
            onClick={() => onPageChange(0)}
            disabled={currentPage === 1 || loading}
        >
            1
        </button>);

        // Show ellipsis if needed
        if (currentPage > 3) {
            buttons.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
        }

        // Show pages around current page
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
            if (i > 1 && i < totalPages) {
                buttons.push(<button
                    key={i}
                    className={`pagination-button ${currentPage === i ? 'active' : ''}`}
                    onClick={() => onPageChange(i - 1)}
                    disabled={loading}
                >
                    {i}
                </button>);
            }
        }

        // Show ellipsis if needed
        if (currentPage < totalPages - 2) {
            buttons.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
        }

        // Always show last page if there's more than one page
        if (totalPages > 1) {
            buttons.push(<button
                key="last"
                className={`pagination-button ${currentPage === totalPages ? 'active' : ''}`}
                onClick={() => onPageChange(totalPages - 1)}
                disabled={currentPage === totalPages || loading}
            >
                {totalPages}
            </button>);
        }

        return buttons;
    };

    return (<MainLayout tab={tab} withSidebar={true}>
            <div className="audit-log-dashboard">
                <div className="card">
                    <div className="card-header">
                        <h1 className="card-title">Audit Logs</h1>
                        <div className="card-actions">
                            <button
                                className="btn btn-outline"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                {showFilters ? 'Hide Filters' : 'Show Filters'}
                            </button>
                            <button
                                className="btn btn-outline"
                                onClick={onRefresh}
                                disabled={loading}
                            >
                                Refresh
                            </button>
                            <button
                                className="btn btn-outline"
                                onClick={onExport}
                                disabled={loading}
                            >
                                Export
                            </button>
                        </div>
                    </div>

                    <div className="card-content">
                        {/* Filters Section */}
                        {showFilters && (<div className="filters-container">
                                <div className="filters-grid">
                                    {/* <div className="filter-item">
                    <label htmlFor="performedBy">Performed By ID</label>
                    <input
                      id="performedBy"
                      type="text"
                      value={localFilters.performedBy || ''}
                      onChange={(e) => handleLocalFilterChange('performedBy', e.target.value)}
                    />
                  </div> */}
                                    <div className="filter-item">
                                        <label htmlFor="performedUsername">Performed By Username</label>
                                        <input
                                            id="performedUsername"
                                            type="text"
                                            value={localFilters.performedUsername || ''}
                                            onChange={(e) => handleLocalFilterChange('performedUsername', e.target.value)}
                                        />
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="action">Action</label>
                                        <select
                                            id="action"
                                            value={localFilters.action || ''}
                                            onChange={(e) => handleLocalFilterChange('action', e.target.value)}
                                        >
                                            <option value="">All Actions</option>
                                            {actions.map((type) => (<option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>))}
                                        </select>
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="performedByRole">Role</label>
                                        <select
                                            id="performedByRole"
                                            value={localFilters.performedByRole || ''}
                                            onChange={(e) => handleLocalFilterChange('performedByRole', e.target.value)}
                                        >
                                            <option value="">All Roles</option>
                                            {roles.map((role) => (<option key={role.value} value={role.value}>
                                                    {role.label}
                                                </option>))}
                                        </select>
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="performedByUserType">User Type</label>
                                        <select
                                            id="performedByUserType"
                                            value={localFilters.performedByUserType || ''}
                                            onChange={(e) => handleLocalFilterChange('performedByUserType', e.target.value)}
                                        >
                                            <option value="">All User Types</option>
                                            {userTypes.map((type) => (<option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>))}
                                        </select>
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="taxNumber">Tax Number</label>
                                        <input
                                            id="taxNumber"
                                            type="text"
                                            value={localFilters.taxNumber || ''}
                                            onChange={(e) => handleLocalFilterChange('taxNumber', e.target.value)}
                                        />
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="activityNumber">Activity Number</label>
                                        <input
                                            id="activityNumber"
                                            type="text"
                                            value={localFilters.activityNumber || ''}
                                            onChange={(e) => handleLocalFilterChange('activityNumber', e.target.value)}
                                        />
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="note">Note</label>
                                        <input
                                            id="note"
                                            type="text"
                                            value={localFilters.note || ''}
                                            onChange={(e) => handleLocalFilterChange('note', e.target.value)}
                                        />
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="affectedUser">Affected Username</label>
                                        <input
                                            id="affectedUser"
                                            type="text"
                                            value={localFilters.affectedUser || ''}
                                            onChange={(e) => handleLocalFilterChange('affectedUser', e.target.value)}
                                        />
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="affectedDeviceName">Affected Device Name</label>
                                        <input
                                            id="affectedDeviceName"
                                            type="text"
                                            value={localFilters.affectedDeviceName || ''}
                                            onChange={(e) => handleLocalFilterChange('affectedDeviceName', e.target.value)}
                                        />
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="startDate">Start Date</label>
                                        <input
                                            id="startDate"
                                            type="date"
                                            value={localFilters.startDate || ''}
                                            onChange={(e) => handleLocalFilterChange('startDate', e.target.value)}
                                        />
                                    </div>
                                    <div className="filter-item">
                                        <label htmlFor="endDate">End Date</label>
                                        <input
                                            id="endDate"
                                            type="date"
                                            value={localFilters.endDate || ''}
                                            onChange={(e) => handleLocalFilterChange('endDate', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="filters-actions">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={resetFilters}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={applyFilters}
                                    >
                                        Apply Filters
                                    </button>
                                </div>
                            </div>)}

                        {/* Active Filters Display */}
                        {renderFilterBadges()}

                        {/* Logs Table */}
                        <div className="table-container">
                            <table className="audit-logs-table">
                                <thead>
                                <tr>
                                    <th onClick={() => handleSort('timestamp')} className="sortable-header">
                                        Timestamp {getSortIndicator('timestamp')}
                                    </th>
                                    <th onClick={() => handleSort('performedBy')} className="sortable-header">
                                        Performed By {getSortIndicator('performedBy')}
                                    </th>
                                    <th onClick={() => handleSort('action')} className="sortable-header">
                                        Action {getSortIndicator('action')}
                                    </th>
                                    <th>Details</th>
                                    <th onClick={() => handleSort('note')} className="sortable-header">
                                        Note {getSortIndicator('note')}
                                    </th>
                                    <th>Status Changes</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (<tr>
                                        <td colSpan={6} className="table-loading">
                                            <div className="loader"></div>
                                        </td>
                                    </tr>) : auditLogs.length === 0 ? (<tr>
                                        <td colSpan={6} className="no-data">
                                            No audit logs found
                                        </td>
                                    </tr>) : (auditLogs.map((log) => (<tr key={log.id}>
                                            <td>{formatDate(log.timestamp)}</td>
                                            <td className="user-role-cell">
                                                <div className="user-info">
                                                    <div
                                                        className="username">{log.performedUsername || log.performedBy || '-'}</div>
                                                    <div className="role-info">
                              <span
                                  className={`role-chip ${getRoleTypeClass(log.performedByRole, log.performedByUserType)}`}>
                                {formatEnum(log.performedByRole)} {log.performedByUserType && `(${formatEnum(log.performedByUserType)})`}
                              </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                          <span className={`action-chip ${getActionTypeClass(log.action)}`}>
                            {formatEnum(log.action)}
                          </span>
                                            </td>
                                            <td className="details-cell">
                                                {log.taxNumber && <div><strong>Tax No:</strong> {log.taxNumber}</div>}
                                                {log.activityNumber &&
                                                    <div><strong>Activity No:</strong> {log.activityNumber}</div>}
                                                {log.affectedUser &&
                                                    <div><strong>Affected User:</strong> {log.affectedUser}</div>}
                                                {log.affectedDeviceName &&
                                                    <div><strong>Device Name:</strong> {log.affectedDeviceName}</div>}
                                                {log.ipAddress &&
                                                    <div><strong>IP Address:</strong> {log.ipAddress}</div>}
                                                <div><strong>Browser:</strong> {log.userAgent && log.userAgent}</div>
                                            </td>
                                            <td>{log.note || '-'}</td>
                                            <td className="status-cell">
                                                {log.previousStatus && log.newStatus ? (<div>
                                                        <div className="status-label">From:</div>
                                                        <div
                                                            className="status-value previous">{log.previousStatus}</div>
                                                        <div className="status-label">To:</div>
                                                        <div className="status-value new">{log.newStatus}</div>
                                                    </div>) : log.newStatus ? (<div>
                                                        <div className="status-label">New:</div>
                                                        <div className="status-value new">{log.newStatus}</div>
                                                    </div>) : '-'}
                                            </td>
                                        </tr>)))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="pagination-container">
                            <div className="page-size-selector">
                                <label htmlFor="pageSize">Rows per page:</label>
                                <select
                                    id="pageSize"
                                    value={pagination.size}
                                    onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>

                            <div className="pagination-info">
                                {pagination.totalElements > 0 ? `${pagination.page * pagination.size + 1}-${Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of ${pagination.totalElements}` : '0 records'}
                            </div>

                            <div className="pagination-controls">
                                {renderPaginationButtons()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>);
};

export default AuditLogDashboard;
